/**
 * Allys Property Feature Section
 * 
 * Theme-specific implementation matching the Airbnb-inspired design
 */

'use client';

import { motion, AnimatePresence } from 'framer-motion';
import Image from 'next/image';
import Link from 'next/link';
import { memo, useState, useEffect } from 'react';

export interface AllysPropertyFeatureSectionProps {
  /**
   * Main heading text
   */
  heading?: string;
  
  /**
   * Main featured property/development data
   */
  mainProperty?: {
    images: string[];  // Array of images to cycle through (shown as thumbnails)
    title: string;
    description: string;
    detailsLink?: string;
  };
  
  /**
   * Secondary property card (individual unit in development)
   */
  secondaryProperty?: {
    image: string;
    price: string;
    ctaText: string;
    ctaLink: string;
  };
  
  /**
   * Additional text below center content
   */
  additionalContent?: string;
  
  /**
   * Auto-cycle interval in milliseconds
   * @default 4000
   */
  cycleInterval?: number;
  
  /**
   * Messages for translations
   */
  messages?: Record<string, unknown>;
  
  /**
   * Current locale
   */
  locale?: 'en' | 'es' | 'fr';
}

// Helper to safely get string from messages
const safeGetString = (obj: unknown, defaultValue: string): string => {
  if (typeof obj === 'string') return obj;
  return defaultValue;
};

/**
 * Allys Property Feature Section Component
 * 
 * Perfect for showcasing property developments with cycling images
 * and featuring individual units/properties on the right.
 */
export const AllysPropertyFeatureSection = memo(function AllysPropertyFeatureSection({
  heading,
  mainProperty,
  secondaryProperty,
  additionalContent,
  cycleInterval = 4000,
  messages,
  locale: _locale
}: AllysPropertyFeatureSectionProps) {
  // Get translations from messages
  const propertyFeature = messages?.propertyFeature as Record<string, unknown> | undefined;
  const secondaryProp = propertyFeature?.secondaryProperty as Record<string, unknown> | undefined;
  
  // Build translated content with fallbacks
  const displayHeading = heading || safeGetString(propertyFeature?.heading, 'Discover Our Latest Development');
  
  const defaultMainProperty = mainProperty || {
    images: [
      "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1200&h=800&fit=crop",
      "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1200&h=800&fit=crop",
      "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1200&h=800&fit=crop"
    ],
    title: safeGetString(propertyFeature?.title, 'Premium Residential Development'),
    description: safeGetString(propertyFeature?.description, 'Experience exceptional architecture and thoughtful design in our newest residential development. Each unit features premium finishes and smart layouts.'),
    detailsLink: "/properties"
  };
  
  const defaultSecondaryProperty = secondaryProperty || {
    image: "https://images.unsplash.com/photo-1600607687644-c7171b42498f?w=600&h=600&fit=crop",
    price: safeGetString(secondaryProp?.price, 'Units Starting at $256K'),
    ctaText: safeGetString(secondaryProp?.ctaText, 'Explore Units'),
    ctaLink: "/properties"
  };
  
  const displayAdditionalContent = additionalContent || safeGetString(propertyFeature?.additionalContent, 'From spacious family homes to modern studios, find the perfect space that fits your lifestyle');
  const viewDevelopmentText = safeGetString(propertyFeature?.viewDevelopment, 'View Development');
  const availableNowText = safeGetString(propertyFeature?.availableNow, 'Available Now');
  
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  // Auto-cycle through images
  useEffect(() => {
    if (isPaused || !defaultMainProperty.images || defaultMainProperty.images.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % defaultMainProperty.images.length);
    }, cycleInterval);

    return () => clearInterval(interval);
  }, [isPaused, cycleInterval, defaultMainProperty.images]);

  const handleThumbnailClick = (index: number) => {
    setCurrentImageIndex(index);
    setIsPaused(false);
  };

  // Safely get current image with fallback
  const currentImage = defaultMainProperty.images[currentImageIndex] || defaultMainProperty.images[0] || '';
  
  // Don't render if no images
  if (!currentImage) {
    return null;
  }

  return (
    <section className="py-16 lg:py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section - Just the heading */}
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-12 lg:mb-16 leading-tight font-inter"
        >
          {displayHeading}
        </motion.h2>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-6">
          {/* Large Property Image with Cycling - Left */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-5"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            <div className="relative aspect-[4/3] rounded-3xl overflow-hidden shadow-lg bg-gray-200">
              {/* Cycling Images with Animation */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentImageIndex}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                  className="absolute inset-0"
                >
                  <Image
                    src={currentImage}
                    alt={`${defaultMainProperty.title} - View ${currentImageIndex + 1}`}
                    fill
                    className="object-cover"
                    priority={currentImageIndex === 0}
                  />
                </motion.div>
              </AnimatePresence>
              
              {/* Clickable Thumbnails */}
              {defaultMainProperty.images && defaultMainProperty.images.length > 1 && (
                <div className="absolute bottom-4 left-4 flex gap-2">
                  {defaultMainProperty.images.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => handleThumbnailClick(index)}
                      className={`w-14 h-14 rounded-full overflow-hidden border-3 shadow-md hover:scale-110 transition-all duration-200 ${
                        currentImageIndex === index 
                          ? 'border-white ring-2 ring-white' 
                          : 'border-white/60 hover:border-white'
                      }`}
                      aria-label={`View image ${index + 1}`}
                    >
                      <Image
                        src={img}
                        alt={`Thumbnail ${index + 1}`}
                        width={56}
                        height={56}
                        className="object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
              
              {/* Image Counter */}
              {defaultMainProperty.images && defaultMainProperty.images.length > 1 && (
                <div className="absolute top-4 right-4 px-3 py-1.5 bg-black/60 backdrop-blur-sm rounded-full">
                  <span className="text-xs font-semibold text-white font-inter">
                    {currentImageIndex + 1} / {defaultMainProperty.images.length}
                  </span>
                </div>
              )}
            </div>
          </motion.div>

          {/* Center Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="lg:col-span-4 flex flex-col justify-center space-y-6"
          >
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 leading-tight font-inter">
              {defaultMainProperty.title}
            </h2>
            
            <p className="text-base text-gray-600 leading-relaxed font-inter">
              {defaultMainProperty.description}
            </p>
            
            {defaultMainProperty.detailsLink && (
              <div>
                <Link
                  href={defaultMainProperty.detailsLink}
                  className="inline-block px-6 py-3 bg-white border-2 border-gray-900 text-gray-900 rounded-lg hover:bg-gray-900 hover:text-white transition-colors duration-200 font-semibold text-sm font-inter"
                >
                  {viewDevelopmentText}
                </Link>
              </div>
            )}
            
            {displayAdditionalContent && (
              <p className="text-sm text-gray-500 leading-relaxed pt-4 font-inter">
                {displayAdditionalContent}
              </p>
            )}
          </motion.div>

          {/* Secondary Property Card - Individual Unit in Development */}
          {defaultSecondaryProperty && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="lg:col-span-3"
            >
              <div className="bg-white rounded-3xl shadow-lg overflow-hidden h-full flex flex-col hover:shadow-xl transition-shadow duration-300">
                <div className="relative aspect-square">
                  <Image
                    src={defaultSecondaryProperty.image}
                    alt="Available unit"
                    fill
                    className="object-cover"
                  />
                  {/* Featured Unit Badge */}
                  <div className="absolute top-3 left-3">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-white/95 backdrop-blur-md shadow-sm font-inter">
                      <span className="inline-block w-1.5 h-1.5 rounded-full mr-1.5 bg-gradient-to-r from-emerald-500 to-teal-600"></span>
                      <span className="text-gray-800">{availableNowText}</span>
                    </span>
                  </div>
                </div>
                
                <div className="p-6 space-y-4 flex-1 flex flex-col">
                  <p className="text-xl font-bold text-gray-900 font-inter">
                    {defaultSecondaryProperty.price}
                  </p>
                  
                  <Link
                    href={defaultSecondaryProperty.ctaLink}
                    className="mt-auto inline-flex items-center justify-center gap-2 px-6 py-3 bg-gray-900 text-white rounded-full hover:bg-gray-800 transition-colors duration-200 font-semibold text-sm font-inter"
                  >
                    {defaultSecondaryProperty.ctaText}
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </div>

        {/* Navigation Arrows - For future carousel of developments */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="flex justify-end gap-3 mt-8"
        >
          <button
            className="w-12 h-12 flex items-center justify-center bg-white border-2 border-gray-200 rounded-full hover:border-gray-900 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label="Previous development"
            disabled
          >
            <svg className="w-5 h-5 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            className="w-12 h-12 flex items-center justify-center bg-white border-2 border-gray-200 rounded-full hover:border-gray-900 transition-colors"
            aria-label="Next development"
          >
            <svg className="w-5 h-5 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </motion.div>
      </div>
    </section>
  );
});

